import { ApolloError } from '@apollo/client';
import { assert, ResponseGraphQLError } from '../../errors';
import { TransactionStatus } from '../../graphql/generated';
import { Recurrence } from '../../types';
import { addBusinessDays, getFirstBusinessDay } from '../../util/businessDays';
import { DT } from '../../util/dateTime';
import { typedKeys } from '../../util/objects';
import { FrequencyValue, GoalDirection, RecurrenceOption } from './types';

export * from './getGoalDirectionWords';
export * from './getTransactionSummaryProps';
export * from './types';
export * from './useIsEditingEnabled';

const TXN_PROCESSING_DURATIONS: Record<
    GoalDirection,
    { min: number; max: number }
> = {
    DEPOSIT: {
        min: 1,
        max: 5,
    },
    WITHDRAWAL: {
        min: 3,
        max: 8,
    },
    GOAL_TO_GOAL: {
        min: 1,
        max: 2,
    },
};

export const getProcessingDuration = (
    direction: GoalDirection
): { min: number; max: number } => {
    return TXN_PROCESSING_DURATIONS[direction];
};

export const getExpectedArrivalDate = (
    startDT: DT,
    direction: GoalDirection
) => {
    const maxBusinessDays = getProcessingDuration(direction).max;
    const maxDate = addBusinessDays(
        getFirstBusinessDay(startDT),
        maxBusinessDays
    );
    return maxDate;
};

export const getExpectedArrivalStr = (
    startDT: DT,
    direction: GoalDirection
) => {
    const maxDate = getExpectedArrivalDate(startDT, direction);
    const arrivalStr = maxDate.format('MMM D, YYYY') + ' by 8pm PST';
    return arrivalStr;
};

const isFailedByTransactionStatus: Record<TransactionStatus, boolean> = {
    PLANNED: false, // Plenty
    QUEUED: false, // Plenty
    REQUESTING: false, // Plenty
    REQUESTED: false, // Atomic
    SCHEDULED: false, // Atomic
    DELAYED: false, // Atomic
    PROCESSING: false, // Atomic
    EXTERNAL_ACCOUNT_DISCONNECTED: false, // Atomic
    EXTERNAL_ACCOUNT_MISSING: false, // Atomic
    EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED: false, // Atomic
    EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE: false, // Atomic
    TRADE_INITIATED: false, // Atomic
    TRADE_SETTLED: false, // Atomic
    TRANSFER_PLANNED: false, // Plenty
    TRANSFER_QUEUED: false, // Plenty
    TRANSFER_REQUESTING: false, // Plenty
    TRANSFER_REQUESTED: false, // Plenty
    COMPLETED: false, // Atomic
    CANCELED: true, // Atomic
    FAILED: true, // Atomic
    EXPIRED: true, // Atomic
    COMMITTED: false, // TODO: Verify this - I'm not sure what it represents
};

const failedSet = new Set(
    typedKeys(isFailedByTransactionStatus).filter(
        (status) => isFailedByTransactionStatus[status]
    )
);

export const isFailedTransactionStatus = (status: TransactionStatus) => {
    return failedSet.has(status);
};

const isObservableByTransactionStatus: Record<TransactionStatus, boolean> = {
    PLANNED: true, // Plenty
    QUEUED: true, // Plenty
    REQUESTING: true, // Plenty
    REQUESTED: true, // Atomic
    SCHEDULED: true, // Atomic
    DELAYED: true, // Atomic
    PROCESSING: true, // Atomic
    EXTERNAL_ACCOUNT_DISCONNECTED: true, // Atomic
    EXTERNAL_ACCOUNT_MISSING: true, // Atomic
    EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED: true, // Atomic
    EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE: true, // Atomic
    TRADE_INITIATED: true, // Atomic
    TRADE_SETTLED: true, // Atomic
    TRANSFER_PLANNED: true, // Plenty
    TRANSFER_QUEUED: true, // Plenty
    TRANSFER_REQUESTING: true, // Plenty
    TRANSFER_REQUESTED: true, // Plenty
    COMPLETED: false, // Atomic
    CANCELED: true, // Atomic
    FAILED: true, // Atomic
    EXPIRED: false, // Atomic
    COMMITTED: true, // TODO: Verify this - I'm not sure what it represents
};

const observableSet = new Set(
    typedKeys(isObservableByTransactionStatus).filter(
        (status) => isObservableByTransactionStatus[status]
    )
);

export const isObservableTransactionStatus = (status: TransactionStatus) => {
    return observableSet.has(status);
};

export const frequencyValueFromRecurrence = (
    recurrence: Recurrence | undefined
): FrequencyValue => {
    if (!recurrence) return 'onetime';

    const { freq, interval } = recurrence;
    if (freq === 'WEEKLY' && interval === 1) return 'weekly';
    if (freq === 'WEEKLY' && interval === 2) return 'biweekly';
    if (freq === 'MONTHLY' && interval === 1) return 'monthly';
    if (freq === 'MONTHLY' && interval === 3) return 'quarterly';
    if (freq === 'YEARLY' && interval === 1) return 'yearly';

    assert(false, 'Invalid recurrence');
};

export const recurrenceFromFrequencyValue = (
    value: FrequencyValue
): Recurrence | undefined => {
    if (value === 'onetime') return undefined;
    if (value === 'weekly') return { freq: 'WEEKLY', interval: 1 };
    if (value === 'biweekly') return { freq: 'WEEKLY', interval: 2 };
    if (value === 'monthly') return { freq: 'MONTHLY', interval: 1 };
    if (value === 'quarterly') return { freq: 'MONTHLY', interval: 3 };
    if (value === 'yearly') return { freq: 'YEARLY', interval: 1 };

    assert(false, 'Invalid frequency value');
};

export const getRecurrenceOptions = ({
    includeJustOnceOption,
    includeQuarterlyOption,
}: {
    includeJustOnceOption: boolean;
    includeQuarterlyOption: boolean;
}): RecurrenceOption[] => {
    let values: FrequencyValue[] = ['weekly', 'biweekly', 'monthly'];

    if (includeQuarterlyOption) {
        values = [...values, 'quarterly'];
    }

    if (includeJustOnceOption) {
        values = ['onetime', ...values];
    }

    return values.map((value) => ({
        value,
        label: labelByFrequencyValue[value],
    }));
};

export const labelByFrequencyValue: Record<FrequencyValue, string> = {
    onetime: 'Just once',
    weekly: 'Every week',
    biweekly: 'Every other week',
    monthly: 'Every month',
    quarterly: 'Every quarter',
    yearly: 'Every year',
};

// TODO: Review these user-facing error messages.
export function depositoryAccountErrorMessageFromApolloError(
    e: ApolloError
): string {
    for (const error of e.graphQLErrors) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { message, extensions } = error as ResponseGraphQLError;
        // TODO: Try to derive better error messages from Plaid errors.
    }
    // TODO: Avoid blocking account selection when this happens,
    // because the error could be unrelated (e.g. network issue)
    return defaultDepositoryAccountErrorMessage;
}

export const defaultDepositoryAccountErrorMessage =
    'This account is currently unavailable. Please use a different account.';
